/**
 * Chart entry form autosave (Phase 18 M10).
 *
 * While a new-entry form is open, its values are saved every 2 s (when changed) to the
 * encrypted draft store (`ChartDrafts`, via `useAutosave`), keyed by visit + entry type.
 * Opening that form again offers "Resume unsaved <type> from HH:MM". Switching forms or
 * unmounting (navigation, idle lock) writes the latest values first. `clear()` after a
 * successful save (or a deliberate discard) removes the draft.
 *
 * `isDirty` (values differ from the blank form) drives `useUnsavedChangesGuard`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAutosave } from './useAutosave';
import { ChartDraft, ChartDrafts } from '../services/offline/chartDrafts';

export const CHART_AUTOSAVE_MS = 2_000;

export interface UseChartFormAutosaveOptions {
  /** Autosave only while true (e.g. the user can edit, the form is a new entry) */
  enabled?: boolean;
  intervalMs?: number;
}

export interface UseChartFormAutosaveResult<T> {
  /** A saved draft for the open form the user has not resumed or discarded yet */
  pendingDraft: ChartDraft<T> | null;
  /** Every draft for the visit (for a "resume" banner while no form is open) */
  visitDrafts: ChartDraft[];
  /** Put the pending draft's values into the form */
  resume: () => void;
  /** Delete the pending draft */
  discard: () => Promise<void>;
  /** Delete the open form's draft (after a successful save, or a confirmed cancel) */
  clear: () => Promise<void>;
  /** The next time this entry type's form opens, apply its draft without asking */
  resumeNext: (entryType: string) => void;
  /** The open form has values not saved to the chart */
  isDirty: boolean;
  /** Last time the draft was written */
  lastSavedAt: Date | null;
}

const toJson = (v: unknown): string => {
  try {
    return JSON.stringify(v ?? null);
  } catch {
    return '';
  }
};

export function useChartFormAutosave<T>(
  visitId: string | null | undefined,
  entryType: string | null | undefined,
  values: T,
  setValues: (v: T) => void,
  { enabled = true, intervalMs = CHART_AUTOSAVE_MS }: UseChartFormAutosaveOptions = {}
): UseChartFormAutosaveResult<T> {
  const key = enabled && visitId && entryType ? `${visitId}:${entryType}` : null;
  const json = useMemo(() => toJson(values), [values]);

  const [pendingDraft, setPendingDraft] = useState<ChartDraft<T> | null>(null);
  const [visitDrafts, setVisitDrafts] = useState<ChartDraft[]>([]);
  const [baseline, setBaseline] = useState<string | null>(null);

  // Latest render's values for timers and handlers
  const latest = useRef({ visitId, entryType, key, json, values });
  latest.current = { visitId, entryType, key, json, values };
  // Last committed render's values: what the form held before a switch / close (see cleanup)
  const committed = useRef(latest.current);
  useEffect(() => { committed.current = { visitId, entryType, key, json, values }; });
  const baselineRef = useRef<string | null>(null);
  const persistedRef = useRef<string | null>(null);
  const resumeNextRef = useRef<string | null>(null);
  const setValuesRef = useRef(setValues);
  setValuesRef.current = setValues;

  const refreshList = useCallback(async (forVisit: string | null | undefined) => {
    if (!forVisit) { setVisitDrafts([]); return; }
    try {
      const rows = await ChartDrafts.listForVisit(forVisit);
      if (latest.current.visitId === forVisit) setVisitDrafts(rows);
    } catch {
      setVisitDrafts([]);
    }
  }, []);

  useEffect(() => { void refreshList(visitId); }, [visitId, refreshList]);

  // A form opened (or switched): take its blank values as the baseline, then look for a draft
  useEffect(() => {
    if (!key || !visitId || !entryType) {
      baselineRef.current = null;
      persistedRef.current = null;
      setBaseline(null);
      setPendingDraft(null);
      return undefined;
    }
    const start = latest.current.json;
    baselineRef.current = start;
    persistedRef.current = start;
    setBaseline(start);
    setPendingDraft(null);
    let live = true;
    ChartDrafts.get<T>(visitId, entryType).then((draft) => {
      if (!live || !draft) return;
      const draftJson = toJson(draft.values);
      if (draftJson === start) return;
      if (resumeNextRef.current === entryType) {
        resumeNextRef.current = null;
        persistedRef.current = draftJson;
        setValuesRef.current(draft.values);
      } else if (latest.current.json === start) {
        setPendingDraft(draft);
      }
    }).catch(() => undefined);

    return () => {
      live = false;
      // Leaving this form (switch, close, unmount): keep what was typed. Cleanups run before the
      // effect above, so `committed` still holds this form's values, not the next form's.
      const cur = committed.current;
      if (cur.key === key && cur.json !== baselineRef.current && cur.json !== persistedRef.current) {
        persistedRef.current = cur.json;
        void ChartDrafts.put(visitId, entryType, JSON.parse(cur.json)).catch(() => undefined);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs per form key only
  }, [key]);

  const save = useCallback(async () => {
    const cur = latest.current;
    if (!cur.key || !cur.visitId || !cur.entryType) return;
    await ChartDrafts.put(cur.visitId, cur.entryType, cur.values);
    persistedRef.current = cur.json;
    setPendingDraft(null); // typing over an older draft replaces it
    void refreshList(cur.visitId);
  }, [refreshList]);

  const { lastSavedAt } = useAutosave({
    save,
    isDirty: () => {
      const cur = latest.current;
      return Boolean(cur.key) && baselineRef.current !== null && cur.json !== baselineRef.current && cur.json !== persistedRef.current;
    },
    intervalMs,
    enabled: Boolean(key),
  });

  const resume = useCallback(() => {
    if (!pendingDraft) return;
    persistedRef.current = toJson(pendingDraft.values);
    setValuesRef.current(pendingDraft.values);
    setPendingDraft(null);
  }, [pendingDraft]);

  const discard = useCallback(async () => {
    const d = pendingDraft;
    setPendingDraft(null);
    if (!d) return;
    await ChartDrafts.remove(d.visitId, d.entryType).catch(() => undefined);
    void refreshList(d.visitId);
  }, [pendingDraft, refreshList]);

  const clear = useCallback(async () => {
    const cur = latest.current;
    // Nothing left to keep: the cleanup on close must not write it back
    baselineRef.current = cur.json;
    persistedRef.current = cur.json;
    setBaseline(cur.json);
    setPendingDraft(null);
    if (!cur.visitId || !cur.entryType) return;
    await ChartDrafts.remove(cur.visitId, cur.entryType).catch(() => undefined);
    void refreshList(cur.visitId);
  }, [refreshList]);

  const resumeNext = useCallback((type: string) => { resumeNextRef.current = type; }, []);

  const isDirty = Boolean(key) && baseline !== null && json !== baseline;

  return { pendingDraft, visitDrafts, resume, discard, clear, resumeNext, isDirty, lastSavedAt };
}

export default useChartFormAutosave;
